import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Star, ExternalLink, Heart, Send } from 'lucide-react';
import api from '@/utils/api';
import { useSuppressModalsOnRoutes } from '@/hooks/useSuppressModalsOnRoutes';

export default function ReviewPrompt() {
  const suppressed = useSuppressModalsOnRoutes();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [willReview, setWillReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [daysUsing, setDaysUsing] = useState(0);
  const [, setGoogleReviewUrl] = useState('');

  useEffect(() => {
    checkReviewPromptStatus();
  }, []);

  const checkReviewPromptStatus = async () => {
    try {
      const response = await api.get('/users/review-prompt/status');
      if (response.data.show_prompt) {
        setDaysUsing(response.data.days_using_app || 0);
        setGoogleReviewUrl(response.data.google_review_url || '');
        // Small delay before showing to let the page load
        setTimeout(() => setOpen(true), 2000);
      }
    } catch (error) {
      console.error('Error checking review prompt status:', error);
    }
  };

  const handleDismiss = async () => {
    try {
      await api.post('/users/review-prompt/dismiss');
      setOpen(false);
    } catch (error) {
      console.error('Error dismissing review prompt:', error);
      setOpen(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/users/review-prompt/feedback?rating=${rating}&feedback=${encodeURIComponent(feedback)}&will_review=${willReview}`);
      
      toast.success('Thank you for your feedback!');
      setOpen(false);

      // If user agreed to leave Google review, open the link
      if (willReview && response.data.google_review_url) {
        window.open(response.data.google_review_url, '_blank');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingText = (r) => {
    switch (r) {
      case 1: return "Poor";
      case 2: return "Fair";
      case 3: return "Good";
      case 4: return "Very Good";
      case 5: return "Excellent";
      default: return "Select a rating";
    }
  };

  return (
    <Dialog open={open && !suppressed} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]" data-testid="review-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Heart className="w-6 h-6 text-red-500 fill-red-500" />
            Enjoying SAFER-i™?
          </DialogTitle>
          <DialogDescription>
            You've been using SAFER-i™ for {String(daysUsing).padStart(2, '0')} days! We'd love to hear your feedback.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Star Rating */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">How would you rate your experience?</Label>
            <div className="flex items-center justify-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110 focus:outline-none"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {getRatingText(hoverRating || rating)}
            </p>
          </div>

          {/* Feedback Text */}
          <div className="space-y-2">
            <Label htmlFor="feedback" className="text-sm font-medium">
              Any feedback or suggestions? (Optional)
            </Label>
            <Textarea
              id="feedback"
              placeholder="Tell us what you love, or how we can improve..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="feedback-textarea"
            />
          </div>

          {/* Google Review Checkbox */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
            <Checkbox
              id="google-review"
              checked={willReview}
              onCheckedChange={(checked) => setWillReview(checked)}
              className="mt-0.5"
              data-testid="google-review-checkbox"
            />
            <div className="space-y-1">
              <Label htmlFor="google-review" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                I'll leave a Google review
                <ExternalLink className="w-4 h-4 text-blue-500" />
              </Label>
              <p className="text-xs text-muted-foreground">
                Your review helps other safety professionals discover SAFER-i™
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={handleDismiss}
            data-testid="maybe-later-btn"
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="gap-2"
            data-testid="submit-feedback-btn"
          >
            {submitting ? (
              <>Submitting...</>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Feedback
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
